import type { ReactNode } from 'react';
import type { Branding } from '../../lib/api/types';
import { assetUrl, cachedBranding } from '../../lib/branding';
import { useServices } from '../../app/services';
import { BrandMark } from '../../components/Icon';
import { LanguagePicker } from '../profile/LanguagePicker';

/** Full-screen centred card used by sign-in and gate screens. */
export function AuthLayout({ branding: given, children, footer }: { branding?: Branding; children: ReactNode; footer?: ReactNode }) {
  const { api } = useServices();
  // Gate screens (PIN, MFA, update) don't load branding themselves: use the cached copy.
  const branding = given ?? cachedBranding();
  const logo = assetUrl(api.baseUrl, branding.logoLightAssetId);
  const logoDark = assetUrl(api.baseUrl, branding.logoDarkAssetId);
  return (
    <div className="pt-safe pb-safe flex min-h-full flex-col bg-bg">
      <div className="flex justify-end p-3">
        <LanguagePicker compact />
      </div>
      <main className="flex flex-1 items-start justify-center px-4 pb-10 sm:items-center">
        <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-border bg-surface p-6 shadow-elevated sm:p-8">
          <div aria-hidden="true" className=" inset-x-0 top-0 h-1 bg-brand absolute" />
          <div className="mb-6 flex flex-col items-center gap-3 text-center">
            {logo ? (
              <>
                <img src={logo} alt={branding.systemName} className={`${logoDark ? 'logo-on-light ' : ''}max-h-16 max-w-[220px] object-contain`} />
                {logoDark && <img src={logoDark} alt={branding.systemName} className="logo-on-dark max-h-16 max-w-[220px] object-contain" />}
              </>
            ) : (
              <BrandMark letter={branding.systemName.slice(0, 1)} size="lg" />
            )}
            {/* A logo already carries the name; show it as text only without one. */}
            <h1 className={logo ? 'sr-only' : 'font-display text-2xl font-bold tracking-tight'}>{branding.systemName}</h1>
          </div>
          {children}
        </div>
      </main>
      {footer && <footer className="pb-6 text-center text-sm text-muted">{footer}</footer>}
    </div>
  );
}
